import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import logoAsset from "@/assets/mowzi-logo.png.asset.json";

const logo = logoAsset.url;

const highlights = [
  "A full week of routed work, already scheduled",
  "100+ sample customers shared across every demo",
  "Crews, billing and reports you can click through",
  "Nothing is sent for real — texts and payments are simulated",
];

/**
 * Public "try it" link. Each visitor gets their own throwaway company that is
 * automatically removed after a couple of days.
 */
export default function DemoPage() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [wantsContact, setWantsContact] = useState(false);
  const [starting, setStarting] = useState(false);

  const start = async () => {
    setStarting(true);
    // Clear any existing (or stale/expired) session first — a broken refresh
    // token in the browser can otherwise block the demo sign-in.
    await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);

    const { data, error } = await supabase.functions.invoke("demo-sandbox", {
      body: {
        company_name: companyName.trim() || undefined,
        email: email.trim() || undefined,
        wants_contact: wantsContact,
      },
    });
    const payload = data as { email?: string; password?: string; error?: string } | null;
    if (error || !payload?.email || !payload?.password) {
      setStarting(false);
      console.error("demo start failed", error, payload);
      return toast.error(
        payload?.error ?? error?.message ?? "Could not start the demo — please try again.",
      );
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: payload.email,
      password: payload.password,
    });
    setStarting(false);
    if (signInError) return toast.error(signInError.message);
    toast.success("Your demo workspace is ready");
    navigate("/dashboard", { replace: true });
  };


  return (
    <main className="flex min-h-screen items-center justify-center bg-frost px-6 py-12">
      <div className="w-full max-w-lg">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <img src={logo} alt="Mowzi" className="h-9 w-9 object-contain" />
          <span className="font-display text-2xl font-bold text-primary">Mowzi</span>
        </Link>

        <Card className="shadow-elevated">
          <CardHeader>
            <CardTitle className="font-display">Try Mowzi — no account needed</CardTitle>
            <CardDescription>
              We'll spin up a private demo workspace loaded with sample customers and a routed
              schedule. It disappears on its own after 48 hours.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <ul className="space-y-2">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>

            <div className="space-y-2">
              <Label htmlFor="companyName">Your company name (optional)</Label>
              <Input
                id="companyName"
                placeholder="Green Acres Lawn Care"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                maxLength={60}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="demoEmail">Work email (optional)</Label>
              <Input
                id="demoEmail"
                type="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
                checked={wantsContact}
                onChange={(e) => setWantsContact(e.target.checked)}
              />
              <span>I'd like Mowzi to reach out about setting up a real account for my company.</span>
            </label>

            <Button className="w-full" onClick={start} disabled={starting}>
              {starting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Building your demo…
                </>
              ) : (
                "Start the demo"
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Ready for the real thing?{" "}
              <Link to="/auth?mode=signup" className="text-primary hover:underline">
                Create your account
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
